import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { EAuthorizationType, EParameterDirection, ETokenSource } from '../models/general-enums';
import { ApiMapperDetailsViewModel, ApiMapperListItem, ApiMapperOutputParam, DataSourceListDDL, DuplicatedDataSource } from '../models/module.model';
import * as datasources from 'src/assets/datasources.json';

@Injectable({
  providedIn: 'root'
})
export class DataSourceService {
  public dataSourcesData: any = (datasources as any).default;
  public isRefreshCalledSubject = new BehaviorSubject<boolean>(false);
  public isRefreshToken$: Observable<boolean> = this.isRefreshCalledSubject.asObservable();

  constructor(private http: HttpClient) { }

  getDatasource(id: number): Observable<ApiMapperDetailsViewModel> {
    return this.http
      .get<ApiMapperDetailsViewModel>(`/ApiInvoker/GetDatasourceById/${id}`);
  }

  /**
   * The data sources of the site. The endpoint returns ApiMapper rows straight
   * from the database, so the field names differ from the detail endpoint and
   * inputs, outputs and path are not included; only the identity fields the
   * pickers and the list need are normalised here.
   */
  getDatasourceList(): Observable<DataSourceListDDL[]> {
    return this.http
      .get<any[]>(`/ApiInvoker/GetDataSourceListBySiteId`)
      .pipe(
        map((rows: any[]) => (rows || []).map((row: any) => ({
          id: +row.id,
          name: row.name
        } as DataSourceListDDL)))
      );
  }

  getApiMapperList(): Observable<ApiMapperListItem[]> {
    return this.http.get<ApiMapperListItem[]>(`/ApiInvoker/GetApiMapperBySiteId`);
  }

  /**
   * Requests logged for a data source. GetApiMapperTransactionHistory filters by
   * log id rather than by ApiMapperId, so the data source name is used as the
   * server-side filter instead.
   */
  getRequestLog(name: string, pageNumber: number, pageSize: number): Observable<any> {
    return this.http.post(`/ApiInvoker/GetApiMapperTransactionHistory`, {
      ApiMapperId: 0,
      SearchText: name || '',
      PageNumber: pageNumber,
      PageSize: pageSize,
      FromDate: null,
      ToDate: null
    });
  }

  /**
   * Output fields of a data source together with their parameter ids, which is
   * what a link to a parent field is keyed by.
   *
   * GetDatasourceById carries the ids on its outputs. Deployments from before
   * that returned outputs by name only, so when no id comes back the ids are
   * read from GetMapperParamsByMapperId instead. That one answers with an
   * ExpandoObject, whose keys stay in Pascal case while the parameters inside
   * are camel cased, so both spellings are accepted.
   */
  getOutputParams(id: number): Observable<ApiMapperOutputParam[]> {
    return this.getDatasource(id).pipe(
      switchMap((source: any) => {
        const outputs = (source && source.outputs) || [];
        const named = outputs
          .map((row: any) => ({
            id: +(row.id || 0),
            paramName: row.name || row.paramName,
            alias: row.alias
          } as ApiMapperOutputParam))
          .filter((row: ApiMapperOutputParam) => !!row.paramName);

        if (named.length && named.every((row: ApiMapperOutputParam) => !!row.id)) {
          return of(named);
        }
        return this.getOutputParamIds(id);
      }),
      catchError(() => this.getOutputParamIds(id))
    );
  }

  /** Output fields read from the params endpoint, the only pre-Id source of ids. */
  private getOutputParamIds(id: number): Observable<ApiMapperOutputParam[]> {
    return this.http
      .get<any>(`/ApiInvoker/GetMapperParamsByMapperId/${id}/${EParameterDirection.Output}`)
      .pipe(
        map((res: any) => {
          const rows = (res && (res.OutParamArray || res.outParamArray)) || [];
          return rows
            .map((row: any) => ({
              id: +(row.id !== undefined ? row.id : row.Id),
              paramName: row.paramName !== undefined ? row.paramName : row.ParamName,
              alias: row.alias !== undefined ? row.alias : row.Alias
            } as ApiMapperOutputParam))
            .filter((row: ApiMapperOutputParam) => !!row.paramName);
        })
      );
  }

  deleteDataSource(id: number): Observable<any> {
    return this.http.delete(`/ApiInvoker/DeleteDataSource/${id}`);
  }

  /**
   * Copies a data source by reading it back in full and saving it as a new one:
   * URL, HTTP method, authorization keys, inputs, outputs and response path all
   * come along. The copy is named "<original> (Copy)", or "(Copy 2)", "(Copy 3)"…
   * when that name is already taken.
   */
  duplicateDataSource(id: number, name?: string): Observable<DuplicatedDataSource> {
    return this.getDatasourceList().pipe(
      switchMap((list: DataSourceListDDL[]) => {
        const all = list || [];
        if (!all.some((item) => +item.id === +id)) {
          return throwError({ error: 'That data source no longer exists.' });
        }

        const copyName = name && name.trim()
          ? name.trim()
          : this.buildCopyName(
            (all.find((item) => +item.id === +id) || { name: '' }).name,
            all.map((item) => item.name)
          );

        return this.getDatasource(id).pipe(
          switchMap((source: ApiMapperDetailsViewModel) => this.http
            .post(`/ApiInvoker/AddDataSource`, this.buildDuplicatePayload(source, copyName))
            .pipe(map((newId: any) => ({ id: +newId, name: copyName, sourceId: +id } as DuplicatedDataSource))))
        );
      })
    );
  }

  /** "Name (Copy)", then "(Copy 2)", "(Copy 3)"… until the name is free. */
  private buildCopyName(sourceName: string, takenNames: string[]): string {
    const base = (sourceName || 'Data source').trim();
    const taken = (takenNames || []).map((item) => (item || '').trim().toLowerCase());
    let counter = 1;

    while (true) {
      const candidate = counter === 1 ? `${base} (Copy)` : `${base} (Copy ${counter})`;
      if (taken.indexOf(candidate.toLowerCase()) === -1) {
        return candidate;
      }
      counter++;
    }
  }

  /** Reshapes a loaded data source into the payload AddDataSource expects. */
  private buildDuplicatePayload(source: any, copyName: string) {
    const authorizationKeys = (source.authorizationKeys || []).map((key: any) => ({
      Key: key.key,
      Value: key.value,
      TokenSource: key.tokenSource
    }));

    return {
      apiMapperViewModel: {
        Id: 0,
        Name: copyName,
        Description: source.description,
        URL: source.url,
        HttpMethod: source.httpMethod,
        MediaTypes: source.mediaTypes,
        Type: source.type,
        Status: 5,
        ApiMapperParams: [],
        IsSecured: true,
        ApiReponseType: source.apiReponseType,
        SiteId: source.siteId,
        ContractName: source.contractName || '',
        ContentType: source.contentType || '',
        Accept: source.accept || '',
        WebMethodName: source.webMethodName || '',
        AuthorizationKeys: authorizationKeys,
        AuthorizationType: source.authorizationType,
        ParentId: null,
        EnableLog: source.enableLog
      },
      inputs: (source.inputs || []).map((input: any) => ({
        id: 0,
        apiMapperId: 0,
        paramName: input.paramName,
        paramOrder: input.paramOrder,
        direction: input.direction,
        paramType: input.paramType,
        sourceType: input.sourceType,
        paramValue: input.paramValue,
        defaultValue: input.defaultValue,
        alias: input.alias
      })),
      // A copy gets its own output rows, so the ids are not carried over.
      outputs: (source.outputs || []).map((output: any) =>
        typeof output === 'string'
          ? { Id: 0, Name: output, Alias: null }
          : { Id: 0, Name: output.name, Alias: output.alias }
      ),
      paths: source.path ? ('' + source.path).split('=>') : [],
      tokenSource: authorizationKeys.length ? authorizationKeys[0].TokenSource : null,
      children: (source.apiMapperParamChild || []).map((link: any, index: number) => ({
        ParamName: link.paramName,
        ApiMapperParamChildId: link.apiMapperParamChildId,
        parentId: link.parentId,
        executionOrder: link.executionOrder !== undefined ? link.executionOrder : index
      }))
    };
  }

  public invokeDataSource(dataSourceId: number, dataSourceParams: any): Observable<any[]> {
    return this.getDataSourceById(dataSourceId).pipe(switchMap((res: { token: string, refreshToken: string, authorizationType: EAuthorizationType, apiUrl?: string }) => {
      if (res && ((res.authorizationType === EAuthorizationType.JWT && res.token !== '') || res.authorizationType === EAuthorizationType.Basic
        || res.authorizationType === EAuthorizationType.None)) {
        let requestOptions = {
          headers: new HttpHeaders({
          }),
        };

        if (res.authorizationType !== EAuthorizationType.Basic && res.authorizationType !== EAuthorizationType.None) {
          requestOptions = {
            headers: new HttpHeaders({
              token: res.token
            }),
          };
        }
        return this.http.post<any>(`/ApiInvoker/Invoke/${dataSourceId}`, dataSourceParams, requestOptions)
          .pipe(
            catchError((err) => {
              if (err.status == "401") {
                this.refreshToken(res.token, res.refreshToken)
                  .subscribe(res => {
                    console.log('ApiInvoker', res);
                    retry();
                  });
              }
              else {
                return throwError(err);
              }
            })
            // catchError((err) => {
            //   if (err.status == "401") {
            //     let isRequestingRefreshToken;
            //     this.isRefreshToken$.pipe(take(1)).subscribe(res => {
            //       isRequestingRefreshToken = res;
            //     })
            //     if (!isRequestingRefreshToken) {
            //       this.isRefreshCalledSubject.next(true);
            //       this.refreshToken(res.token, res.refreshToken)
            //         .subscribe(res => {
            //           console.log(res);
            //           this.isRefreshCalledSubject.next(false);
            //           retry();
            //         });
            //     } else {
            //       // let subscription =
            //       this.isRefreshToken$.subscribe(res => {
            //         if (!res) {
            //           retry();
            //         }
            //       })

            //       // subscription.unsubscribe();
            //     }
            //   }
            //   else {
            //     return throwError(err);
            //   }
            // })
          )
      }
    }));
  }

  public refreshToken(token: string, refreshToken: string) {
    const data = {
      Token: token,
      RefreshToken: refreshToken,
      inputType: ["Token", "RefreshToken"],

    };
    return this.invokeDataSource(this.dataSourcesData['refreshToken'], data).pipe(
      shareReplay(10),
      tap(res => {
        if (res) {
          const coreData = res['content']
          localStorage.setItem('coreToken', coreData['Token']);
          localStorage.setItem('coreRefreshToken', coreData['RefreshToken']);
        }
      })

    );
  }

  public getDataSourceById(dataSourceId: number) {
    return this.http.get<ApiMapperDetailsViewModel>(`/ApiInvoker/GetApiMapperById/${dataSourceId}`).pipe(
      switchMap((result: ApiMapperDetailsViewModel) => {
        let resultObs: Observable<any>;
        let token = result.authorizationKeys.find(x => x.key == 'LocalStorageToken');
        let refreshToken = result.authorizationKeys.find(x => x.key == 'LocalStorageRefreshToken');
        switch (result.authorizationType) {
          case EAuthorizationType.JWT:
            {
              switch (token.tokenSource) {
                case ETokenSource.DataBase:
                  {
                    this.http.get(`/ApiInvoker/GetAuthentication/${token.value}/${refreshToken.value}`).subscribe((res: any[]) => {
                      let tokenDB = res.find(x => x.propertyName == token.value);
                      let refreshTokenDB = res.find(x => x.propertyName == refreshToken.value);
                      resultObs = of({
                        token: tokenDB.value,
                        authorizationType: EAuthorizationType.JWT,
                        apiUrl: result.url,
                        refreshToken: refreshTokenDB.value
                      })
                    });
                    break;
                  }
                case ETokenSource.LocalStorage:
                  {
                    resultObs = of({
                      token: localStorage.getItem(token.value),
                      authorizationType: EAuthorizationType.JWT,
                      apiUrl: result.url,
                      refreshToken: localStorage.getItem(refreshToken.value)
                    });
                    break;
                  }
              }
              break;
            }
          case EAuthorizationType.Basic:
            {
              resultObs = of({
                token: '',
                authorizationType: EAuthorizationType.Basic,
                apiUrl: null,
                refreshToken: ''
              });
              break;
            }
          case EAuthorizationType.None:
            {
              resultObs = of({
                token: '',
                authorizationType: EAuthorizationType.None,
                apiUrl: null,
                refreshToken: ''
              });
              break;
            }
        }
        return resultObs;
      }
      ))
  }
}
